/**
 * OPARA sayı formatlama yardımcıları
 * Kural D: <2000 tam, 2000-999999 → K, ≥1M → M
 */

/**
 * Ham sayıyı Kural D'ye göre formatlar.
 * - num < 2000: tam (1,065 → "1,065")
 * - 2000 ≤ num < 1,000,000: K kısaltma (13,618 → "13.6K", 75,000 → "75K")
 * - num ≥ 1,000,000: M kısaltma (1,400,000 → "1.4M", 3,000,000 → "3M")
 */
export const formatNumberRuleD = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  const n = Math.abs(Number(num));
  const sign = num < 0 ? '-' : '';

  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return sign + (m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)) + 'M';
  }
  if (n >= 20_000) {
    // 20K üstü: tam yuvarla (75K, 102K, 941K)
    return sign + Math.round(n / 1_000) + 'K';
  }
  if (n >= 2_000) {
    // 2K-20K: 1 ondalık (13.6K, 19.5K)
    const k = n / 1_000;
    return sign + (k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)) + 'K';
  }
  return sign + Math.round(n).toLocaleString('en-US');
};

/**
 * Takipçi sayısı için akıllı format.
 * follower_count_source API response'unda olmadığı için heuristic kullanılıyor:
 *   - num % 1000 === 0 ise (örn 3,000,000) → yuvarlanmış kabul → kısalt ("3M")
 *   - Aksi halde (örn 3,024,732) → tam göster ("3,024,732")
 *
 * NOT: Eğer ileride backend follower_count_source field'ı response'a eklerse,
 * isPrecise parametresini explicit kullanabilir.
 */
export const formatFollowerCount = (num, isPrecise = undefined) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  const n = Math.round(num);

  // Explicit flag varsa onu kullan
  if (isPrecise === true) return n.toLocaleString('en-US');
  if (isPrecise === false) return formatNumberRuleD(n);

  // Heuristic: 1000'in tam katı ise yuvarlanmış kabul et
  if (n >= 1000 && n % 1000 === 0) {
    return formatNumberRuleD(n);
  }
  return n.toLocaleString('en-US');
};

/**
 * Yüzde formatı (Amerikan: 0.73%, % sağda).
 */
export const formatPercent = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return Number(num).toFixed(decimals) + '%';
};

/**
 * "min - max" aralık formatı, her iki uçta Kural D.
 */
export const formatRange = (min, max) => {
  if (min === null || min === undefined || max === null || max === undefined) return '-';
  return `${formatNumberRuleD(min)} - ${formatNumberRuleD(max)}`;
};

/**
 * Link tıklama aralık string'ini parse edip ortalamayı hesaplar.
 * Backend "852 - 1,278" string'i veya benzeri formatta dönüyor.
 * Returns: integer (1065) veya null (parse edilemezse)
 */
export const calculateAverageFromRange = (rangeStr) => {
  if (!rangeStr || typeof rangeStr !== 'string') return null;

  // "852 - 1,278" veya "852-1278" veya "1.2K - 1.5K" gibi formatları yakala
  const match = rangeStr.match(/(\d[\d.,]*\s*[KMkm]?)\s*[-–—]\s*(\d[\d.,]*\s*[KMkm]?)/);
  if (!match) return null;

  const parseValue = (s) => {
    s = s.trim().replace(/,/g, '');
    const numMatch = s.match(/^([\d.]+)\s*([KMkm]?)$/);
    if (!numMatch) return null;
    let val = parseFloat(numMatch[1]);
    const suffix = numMatch[2].toUpperCase();
    if (suffix === 'K') val *= 1000;
    else if (suffix === 'M') val *= 1_000_000;
    return val;
  };

  const min = parseValue(match[1]);
  const max = parseValue(match[2]);
  if (min === null || max === null || isNaN(min) || isNaN(max)) return null;

  return Math.round((min + max) / 2);
};
